import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import GlobalHeader from '../components/GlobalHeader';
import Footer from '../components/Footer';
import { Book, WarningCircle } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/pages/${slug}`)
      .then((r) => {
        setPage(r.data);
        
        // Apply SEO meta tags
        document.title = r.data.seo_title || `${r.data.title} — Kavyalekh`;

        const setMeta = (name, content) => {
          if (!content) return;
          let el = document.querySelector(`meta[name="${name}"]`);
          if (!el) {
            el = document.createElement('meta');
            el.setAttribute('name', name);
            document.head.appendChild(el);
          }
          el.setAttribute('content', content);
        };

        const setProperty = (property, content) => {
          if (!content) return;
          let el = document.querySelector(`meta[property="${property}"]`);
          if (!el) {
            el = document.createElement('meta');
            el.setAttribute('property', property);
            document.head.appendChild(el);
          }
          el.setAttribute('content', content);
        };

        // Basic SEO
        setMeta('description', r.data.seo_description || r.data.excerpt);
        setMeta('keywords', r.data.seo_keywords?.join(', ') || '');
        setMeta('robots', r.data.meta_robots || 'index, follow');
        
        // Canonical URL
        if (r.data.canonical_url) {
          let link = document.querySelector('link[rel="canonical"]');
          if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            document.head.appendChild(link);
          }
          link.setAttribute('href', r.data.canonical_url);
        }

        // Open Graph
        setProperty('og:title', r.data.og_title || r.data.title);
        setProperty('og:description', r.data.og_description || r.data.excerpt);
        setProperty('og:type', r.data.og_type || 'website');
        setProperty('og:url', window.location.href);
        if (r.data.og_image || r.data.featured_image) {
          setProperty('og:image', r.data.og_image || r.data.featured_image);
        }

        // Twitter Card
        setMeta('twitter:card', r.data.twitter_card || 'summary_large_image');
        setMeta('twitter:title', r.data.twitter_title || r.data.og_title || r.data.title);
        setMeta('twitter:description', r.data.twitter_description || r.data.og_description || r.data.excerpt);
        if (r.data.twitter_image || r.data.og_image || r.data.featured_image) {
          setMeta('twitter:image', r.data.twitter_image || r.data.og_image || r.data.featured_image);
        }
      })
      .catch((err) => {
        setError(err.response?.status === 404 ? 'Page not found' : 'Failed to load page');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-2/3 mx-auto mb-4"></div>
            <div className="h-4 bg-muted rounded w-1/2 mx-auto mb-8"></div>
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded"></div>
              <div className="h-4 bg-muted rounded"></div>
              <div className="h-4 bg-muted rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <GlobalHeader />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <WarningCircle size={64} className="mx-auto text-muted-foreground mb-4" weight="duotone" />
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {error}
          </h1>
          <p className="text-muted-foreground mb-6">The page you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90">
            <Book size={20} /> Go to Homepage
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />
      
      {/* Page Header */}
      <div className="bg-card border-b border-border py-12 px-6">
        <div className="max-w-4xl mx-auto">
          {page.category && (
            <p className="text-xs uppercase tracking-widest text-primary/70 mb-2">{page.category}</p>
          )}
          <h1
            className="text-4xl md:text-5xl font-bold mb-3"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {page.title}
          </h1>
          {page.excerpt && <p className="text-lg text-muted-foreground">{page.excerpt}</p>}
        </div>
      </div>

      {/* Page Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <article
          className="prose prose-lg max-w-none"
          style={{
            fontFamily: 'IBM Plex Sans, sans-serif',
            lineHeight: '1.75',
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content) }}
        />

        {/* Page Footer */}
        <div className="mt-12 pt-8 border-t border-border text-sm text-muted-foreground">
          <p>Last updated: {new Date(page.updated_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
